import { Button } from "@chakra-ui/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { FiDownload } from "react-icons/fi";
import { useDocumentDownloader } from "./useDocumentDownloader";

interface Props {
  documentId: string;
  isSignedDocument?: boolean;
  // When set, downloads via the sourcing-event-scoped attachment endpoint instead of the generic one.
  eventId?: string;
}

/**
 * Labelled sibling of [DownloadDocumentIconButton]. An icon alone, placed left of a file name, reads
 * as a decorative file-type icon rather than an action — so anywhere a row can carry a real button,
 * this is the one to use (matching the procurement-documents rows).
 */
export const DownloadDocumentButton = ({ documentId, isSignedDocument, eventId }: Props) => {
  const { t } = useTranslation();
  const { getDownloadLink, getSeAttachmentDownloadLink } = useDocumentDownloader();
  const [isCreatingLink, setIsCreatingLink] = useState(false);

  const download = async () => {
    setIsCreatingLink(true);
    try {
      const url = eventId
        ? await getSeAttachmentDownloadLink(eventId, documentId)
        : await getDownloadLink(documentId, isSignedDocument ?? false);
      if (url) window.open(url, "_blank", "noopener,noreferrer");
    } catch {
      // useDocumentDownloader has already surfaced the failure to the user.
    } finally {
      setIsCreatingLink(false);
    }
  };

  return (
    <Button
      size="sm"
      variant="outline"
      colorScheme="gray"
      leftIcon={<FiDownload />}
      onClick={() => void download()}
      isLoading={isCreatingLink}
      flexShrink="0"
    >
      {t("Download")}
    </Button>
  );
};
